import { Plus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { leadFormPath } from "../../routes";
import { Footer } from "../Footer";

const dentalFaqs = [
  {
    question: "Will it feel like talking to a real receptionist?",
    answer: "ClawEase follows what a patient is asking, asks for missing details, and helps them work toward a booking in a natural conversation. Patients can explain what they need in their own words, while your team stays focused on the people in your practice.",
  },
  {
    question: "What happens when patients contact us after hours?",
    answer: "ClawEase can respond 24/7 across your connected channels, including evenings and weekends. Patients can get answers about your practice and move ahead with a booking even when the front desk is closed.",
  },
  {
    question: "How does an inquiry become a confirmed appointment?",
    answer: "ClawEase gathers the visit details, checks available times in your connected calendar or booking system, and helps the patient choose a suitable appointment. When booking is enabled, it completes the booking and keeps the outcome with the conversation.",
  },
  {
    question: "Will it work with the systems we already use?",
    answer: "Keep your current PMS. ClawEase can connect the steps your practice needs through its available tools and permissions. If you use Google Calendar instead, built-in calendar support lets patients book without a PMS.",
  },
  {
    question: "Can we use it beyond phone calls?",
    answer: "Yes. Bring phone, email, WhatsApp and web chat into one place, and connect your own custom channels. Phone, email and web booking are included in Free; WhatsApp and additional channels are available with Pro and Team.",
  },
  {
    question: "How does ClawEase learn about our practice?",
    answer: "ClawEase uses AI-powered exploration to understand your existing systems and how your practice works. During this process, it aligns with you on your requirements in a one-time discussion, then uses that understanding to configure your workflows.",
  },
  {
    question: "What if a patient needs to speak to our team?",
    answer: "ClawEase can pass the request and conversation details to your team through the handoff path you configure. Your staff can see what the patient needs and continue from there, with clinical questions and special requests directed to the right people.",
  },
  {
    question: "Can we see what was said and what got done?",
    answer: "Yes. The conversation view brings together messages, booking outcomes and handoff details across your connected channels. Staff can review the full history and see what still needs attention without piecing together separate inboxes.",
  },
  {
    question: "Which languages can our patients use?",
    answer: "Free supports English and Chinese. Pro and Team support 25 languages, helping patients ask questions and arrange appointments in a language they are comfortable using.",
  },
  {
    question: "Can it follow up with patients who have not booked?",
    answer: "Pro and Team include automatic follow-up after an inquiry. You can use it to continue the conversation and help interested patients take the next step toward a booking, following your practice's workflow.",
  },
  {
    question: "How do we get started with ClawEase?",
    answer: "Download ClawEase and give it access to the systems you want to connect. It explores your systems, understands your needs, and handles the integration for you.",
    contactSupport: true,
  },
  {
    question: "Can we try ClawEase before paying?",
    answer: "Yes. Free requires no subscription and includes $20 in welcome credits plus $5 in credits each month. You can try everyday inquiries and booking before choosing Pro for ongoing automation or Team for shared access and collaboration.",
  },
];

export function DentalFaq() {
  const { search } = useLocation();
  return (
    <section id="dental-faq" aria-labelledby="dental-faq-title" className="scroll-mt-24 pt-12 sm:pt-16 lg:pt-16">
      <div>
        <div className="dental-faq-content pb-10 sm:pb-12">
          <div className="dental-content">
            <h2 id="dental-faq-title" className="text-balance text-center text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
              Your questions, answered.
            </h2>
            <div className="mt-8 grid gap-x-12 md:grid-cols-2 lg:mt-10 lg:gap-x-16">
              {[dentalFaqs.slice(0, 6), dentalFaqs.slice(6)].map((column, index) => (
                <div key={index} className={index === 0 ? "border-t border-[#e3dce9]" : "md:border-t md:border-[#e3dce9]"}>
                  {column.map(({ question, answer, contactSupport }) => (
                    <details key={question} name="dental-faq" className="group border-b border-[#e3dce9]">
                      <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-5 py-4 text-[14px] font-bold leading-6 text-[#293344] transition-colors hover:text-[#6d35cf] focus-visible:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#6d35cf] sm:text-[15px] [&::-webkit-details-marker]:hidden">
                        <span>{question}</span>
                        <Plus aria-hidden="true" className="h-4 w-4 shrink-0 text-[#6d35cf] transition-transform group-open:rotate-45 motion-reduce:transition-none" />
                      </summary>
                      <p className="pb-5 pr-8 text-[14px] leading-6 text-[#667280]">
                        {answer}
                        {contactSupport && (
                          <>
                            {" "}For special requirements, <Link to={leadFormPath("en", undefined, "dental_faq_support", search)} className="rounded-sm font-semibold text-[#6d35cf] no-underline hover:text-[#50218f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6d35cf]">contact us</Link> and a dedicated technical specialist will work with you one to one.
                          </>
                        )}
                      </p>
                    </details>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <Footer lang="en" compact />
      </div>
    </section>
  );
}
